import { useQuery } from '@tanstack/react-query';
import { axiosGet } from '../api/axios';
import { toast } from 'react-toastify';
import {
  calculateHoursActiveDuringSchedule,
  formatScheduledStart,
  formatDateTime,
  secondsToHours,
} from '../containers/peer-schedule/schedule-history/formatting-helpers';
import moment from 'moment';

export interface ScheduleOverviewResponse {
  time_slots: {
    [start_at: string]: TimeSlot;
  };
}

export interface TimeSlot {
  end_at: string;
  listener_display_names: string[];
  start_at: string;
}

export interface ScheduleOverviewOptions {
  domainId?: number | null;
}

const getScheduleOverview = async (options?: ScheduleOverviewOptions): Promise<ScheduleOverviewResponse> => {
  const params = options?.domainId != null && options.domainId !== undefined ? { domain_id: options.domainId } : {};
  const response = await axiosGet('/schedule_reporting/overview', params, 'v3');
  return response.data;
};

export const useScheduleOverview = (options?: ScheduleOverviewOptions) => {
  const domainId = options?.domainId ?? null;
  const { data, isLoading, error, refetch, isFetching } = useQuery<ScheduleOverviewResponse>(
    ['scheduleOverview', domainId],
    () => getScheduleOverview(options),
  );
  return { data, isLoading, error, refetch, isFetching };
};

const getScheduleOverviewDownload = async (
  options?: ScheduleOverviewOptions,
): Promise<{ blob: Blob; filename: string }> => {
  const params = options?.domainId != null && options.domainId !== undefined ? { domain_id: options.domainId } : {};
  const response = await axiosGet('/schedule_reporting/overview/download', params, 'v3');
  const contentDisposition = response.headers['content-disposition'];
  const filenameMatch = contentDisposition?.match(/filename=(.*\.csv)/);
  const filename = filenameMatch![1];
  return {
    blob: new Blob([response.data], { type: 'text/csv' }),
    filename,
  };
};

export const useScheduleOverviewDownload = (enabled: boolean, options?: ScheduleOverviewOptions) => {
  const domainId = options?.domainId ?? null;
  const { data, isLoading, error, refetch, isFetching } = useQuery<{ blob: Blob; filename: string }>(
    ['scheduleOverviewDownload', domainId],
    () => getScheduleOverviewDownload(options),
    {
      enabled,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        const downloadLink = document.createElement('a');
        const url = URL.createObjectURL(data.blob);
        downloadLink.href = url;
        downloadLink.download = data.filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(url);
        toast.success('Schedule overview downloaded successfully');
      },
    },
  );
  return { data, isLoading, error, refetch, isFetching };
};

export interface ScheduleHistoryParams {
  listener_role_ids: number[];
  start_date: string;
  end_date: string;
}

export interface ScheduleHistory {
  active_duration: number;
  display_name: string;
  ended_at: string;
  listener_role_id: string;
  schedule_created_at: string;
  schedule_day: string;
  scheduled_duration: number;
  scheduled_end: string;
  scheduled_start: string;
  started_at: string;
  timezone: string;
  formattedScheduledStart: string;
  formattedScheduledEnd: string;
  formattedActualStart: string;
  formattedActualEnd: string;
  hoursActiveDuringSchedule: string | number;
  formattedScheduleDuration: string | number;
  formattedActiveDuration: string | number;
  formattedScheduleDay: string;
}

const getScheduleHistory = async (params: ScheduleHistoryParams): Promise<ScheduleHistory[]> => {
  const { listener_role_ids, start_date, end_date } = params;

  let url = `/availabilities/history_table?start_date=${start_date}&end_date=${end_date}`;

  listener_role_ids.forEach((id) => {
    url += `&listener_role_ids=${id}`;
  });

  const response = await axiosGet(url, {}, 'v3');
  return response.data;
};

export const useScheduleHistory = (params: ScheduleHistoryParams, timezoneDisplay: string) => {
  const isParamsValid = params.listener_role_ids.length > 0 && Boolean(params.start_date) && Boolean(params.end_date);

  const { data, isLoading, error, refetch, isFetching } = useQuery<ScheduleHistory[]>(
    ['scheduleHistory', params],
    () => getScheduleHistory(params),
    {
      enabled: isParamsValid,
      refetchOnWindowFocus: isParamsValid,
      select: (rawData) =>
        rawData.map((history) => ({
          ...history,
          formattedScheduledStart: formatScheduledStart(
            history.scheduled_start,
            history.timezone,
            history.schedule_day,
            timezoneDisplay,
          ),
          formattedScheduledEnd: formatScheduledStart(
            history.scheduled_end,
            history.timezone,
            history.schedule_day,
            timezoneDisplay,
          ),
          formattedActualStart: formatDateTime(
            history.started_at,
            history.timezone,
            history.schedule_day,
            timezoneDisplay,
          ),
          formattedActualEnd: formatDateTime(history.ended_at, history.timezone, history.schedule_day, timezoneDisplay),
          hoursActiveDuringSchedule: calculateHoursActiveDuringSchedule(history),
          formattedScheduleDuration: secondsToHours(history.scheduled_duration),
          formattedActiveDuration: secondsToHours(history.active_duration),
          formattedScheduleDay: moment(history.schedule_day).format('dddd, MM/DD/YYYY'),
        })),
    },
  );

  return {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
    isParamsValid,
  };
};

const getScheduleHistoryDownload = async (params: ScheduleHistoryParams): Promise<{ blob: Blob; filename: string }> => {
  const { listener_role_ids, start_date, end_date } = params;

  let url = `/availabilities/history_table/download?start_date=${start_date}&end_date=${end_date}`;

  listener_role_ids.forEach((id) => {
    url += `&listener_role_ids=${id}`;
  });

  const response = await axiosGet(url, {}, 'v3');
  const contentDisposition = response.headers['content-disposition'];
  const filenameMatch = contentDisposition?.match(/filename=(.*\.csv)/);
  const filename = filenameMatch![1];
  return {
    blob: new Blob([response.data], { type: 'text/csv' }),
    filename,
  };
};

export const useScheduleHistoryDownload = (params: ScheduleHistoryParams, enabled: boolean) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<{ blob: Blob; filename: string }>(
    ['scheduleHistoryDownload', params],
    () => getScheduleHistoryDownload(params),
    {
      enabled,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        const downloadLink = document.createElement('a');
        const url = URL.createObjectURL(data.blob);
        downloadLink.href = url;
        downloadLink.download = data.filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(url);
        toast.success('Schedule history downloaded successfully');
      },
    },
  );

  return { data, isLoading, error, refetch, isFetching };
};
